import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { WhatsAppIcon } from "../common/WhatsAppIcon";
import { TurkishFlag } from "../common/TurkishFlag";
import { NAV_LINKS, LOGO_BLACK, LOGO_WHITE } from "../../data/site";
import { useWa } from "../../context/SiteData";

const Logo = ({ dark }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
    <img
      src={dark ? LOGO_WHITE : LOGO_BLACK}
      alt="Yalı Yurt — Samsun Erkek Öğrenci Yurdu"
      className="h-10 w-auto transition-opacity duration-300 sm:h-12"
    />
    <TurkishFlag className="flag-wave h-3.5 w-auto shrink-0 sm:h-4" />
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const wa = useWa();

  // "#" bağlantıları: ana sayfadaki bölüme yumuşak kaydır
  const goHash = (e, to) => {
    e.preventDefault();
    const id = to.split("#")[1];
    setOpen(false);
    if (pathname !== "/") {
      navigate("/#" + id);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    }
  };
  const isHome = pathname === "/";
  const dark = isHome && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          dark
            ? "bg-transparent"
            : "border-b border-line bg-cream-50/95 backdrop-blur-md"
        }`}
      >
        <div
          className={`container-x flex items-center justify-between transition-all duration-500 ${
            scrolled ? "h-[4.5rem]" : "h-[5.5rem]"
          }`}
        >
          <Logo dark={dark} />

          <nav className="hidden items-center gap-7 xl:gap-8 lg:flex" data-testid="nav-menu">
            {NAV_LINKS.map((l) =>
              l.to.includes("#") ? (
                <a
                  key={l.to}
                  href={l.to}
                  onClick={(e) => goHash(e, l.to)}
                  data-testid="nav-link-neden-biz"
                  className={`text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    dark ? "text-cream-50/65 hover:text-cream-50" : "text-ink-500 hover:text-ink-900"
                  }`}
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    dark
                      ? isActive
                        ? "text-cream-50"
                        : "text-cream-50/65 hover:text-cream-50"
                      : isActive
                        ? "text-ink-900"
                        : "text-ink-500 hover:text-ink-900"
                  }`
                }
                >
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className={`hidden items-center gap-2.5 px-5 py-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 rounded-[2px] md:inline-flex ${
                dark
                  ? "bg-cream-50 text-ink-900 hover:bg-cream-300"
                  : "bg-ink-900 text-cream-50 hover:bg-ink-600"
              }`}
            >
              <WhatsAppIcon size={16} />
              Bilgi Al
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-toggle"
              aria-label="Menü"
              className={`lg:hidden transition-colors duration-300 ${
                dark ? "text-cream-50" : "text-ink-900"
              }`}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-cream-50 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="container-x flex h-full flex-col justify-center gap-1 pt-16">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  {l.to.includes("#") ? (
                    <a
                      href={l.to}
                      onClick={(e) => goHash(e, l.to)}
                      data-testid="mobile-nav-link-neden-biz"
                      className="block border-b border-line py-4 text-2xl font-bold tracking-tight text-ink-500"
                    >
                      {l.label}
                    </a>
                  ) : (
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `block border-b border-line py-4 text-2xl font-bold tracking-tight ${
                          isActive ? "text-ink-900" : "text-ink-500"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
              <motion.a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="btn-primary mt-8 self-start"
              >
                <WhatsAppIcon size={16} />
                Bilgi Al
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
